import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  VStack,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Button,
  HStack,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useLeague } from '../context/LeagueContext';
import { Match } from '../types';
import { MUNDIAL_GROUPS, MundialTeam } from '../types/mundial';
import { mundialTeamService, getTeamsForGroup } from '../services/mundial/mundialTeamService';
import { mundialMatchService } from '../services/mundial/mundialMatchService';
import { calculateGroupStandings } from '../services/mundial/mundialStandingsService';
import {
  allGroupStagesComplete,
  computeQualifiedTeams,
} from '../services/mundial/mundialQualificationService';
import { buildBracketDisplay } from '../services/mundial/mundialBracketService';
import { GroupStandingsTable } from '../components/mundial/GroupStandingsTable';
import { KnockoutBracket } from '../components/mundial/KnockoutBracket';
import { MundialMatchCalendar } from '../components/mundial/MundialMatchCalendar';
import { MundialHistorySection } from '../components/mundial/MundialHistorySection';
import { KNOCKOUT_ROUNDS } from '../constants/mundialBracket';
import { MundialMatchType } from '../types/mundial';
import { SportsPageShell } from '../components/ui/SportsPageShell';
import { SportsCard } from '../components/ui/SportsCard';
import { useAppColors } from '../hooks/useAppColors';

const TAB_INDEX: Record<string, number> = {
  grupos: 0,
  calendario: 1,
  eliminatorias: 2,
  historia: 3,
};

export const MundialPage: React.FC = () => {
  const { league } = useLeague();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab')?.toLowerCase() ?? '';
  const defaultTabIndex = TAB_INDEX[tabParam] ?? 0;
  const [teams, setTeams] = useState<MundialTeam[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const { accent, subtle } = useAppColors();

  const loadData = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    try {
      const [teamsData, matchesData] = await Promise.all([
        mundialTeamService.getAllTeams(),
        mundialMatchService.getMundialMatchesHydrated(),
      ]);
      setTeams(teamsData);
      setMatches(matchesData);
    } finally {
      if (!options?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (league === 'mundial_fifa') loadData();
  }, [league, loadData]);

  if (league !== 'mundial_fifa') {
    return (
      <SportsPageShell title="Mundial FIFA 2026" subtitle="Selecciona esta competencia en el menú">
        <Alert status="info">
          <AlertIcon />
          Esta vista es solo para el Mundial FIFA 2026.
        </Alert>
      </SportsPageShell>
    );
  }

  if (loading) {
    return (
      <Center py={24}>
        <Spinner size="xl" color="pitch.500" />
      </Center>
    );
  }

  const teamsByCode = mundialTeamService.getTeamsByCode(teams);
  const groupMatches = matches
    .filter((m) => m.match_type === 'group')
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const { qualified } = computeQualifiedTeams(teams, matches);
  const groupsComplete = allGroupStagesComplete(teams, matches);

  const matchesByRound: Record<string, ReturnType<typeof buildBracketDisplay>> = {};
  KNOCKOUT_ROUNDS.forEach((round) => {
    matchesByRound[round.matchType] = buildBracketDisplay(
      matches,
      teams,
      round.matchType as MundialMatchType
    );
  });

  const champion = matchesByRound.final?.[0];
  const championTeam = champion?.winnerCode && teamsByCode.get(champion.winnerCode);

  return (
    <SportsPageShell
      title="Mundial FIFA 2026"
      subtitle="Grupos, calendario, eliminatorias e historia"
      badge="CENTRO DE TORNEO"
      maxW="100%"
      contentPx={{ base: 3, md: 6, lg: 8 }}
      actions={
        <Button as={RouterLink} to="/" size="sm" colorScheme="gold">
          Capturar quiniela
        </Button>
      }
    >
      <VStack spacing={6} align="stretch">
        {championTeam && (
          <SportsCard accent>
            <HStack>
              <Text fontWeight="bold">Campeón del torneo:</Text>
              <Text color={accent} fontWeight="bold" fontSize="lg">
                {championTeam.name}
              </Text>
            </HStack>
          </SportsCard>
        )}

        {qualified.length > 0 && (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            {groupsComplete
              ? `${qualified.length} equipos clasificados a eliminatorias (24 + 8 mejores terceros)`
              : `${qualified.length} clasificados parciales — faltan resultados`}
          </Alert>
        )}

        <Tabs variant="sports" colorScheme="pitch" isLazy defaultIndex={defaultTabIndex} key={defaultTabIndex}>
          <TabList flexWrap="wrap">
            <Tab>Fase de grupos</Tab>
            <Tab>Calendario</Tab>
            <Tab>Eliminatorias</Tab>
            <Tab>Historia FIFA</Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0} pt={6}>
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                {MUNDIAL_GROUPS.map((groupId) => {
                  const groupTeams = getTeamsForGroup(teams, groupId);
                  if (groupTeams.length === 0) return null;
                  const gMatches = groupMatches.filter(
                    (m) => m.group?.toUpperCase() === groupId
                  );
                  const standings = calculateGroupStandings(groupTeams, gMatches);
                  return (
                    <GroupStandingsTable
                      key={groupId}
                      groupId={groupId}
                      standings={standings}
                    />
                  );
                })}
              </SimpleGrid>
            </TabPanel>

            <TabPanel px={0} pt={6}>
              <SportsCard>
                {groupMatches.length === 0 ? (
                  <Text color={subtle}>No hay partidos de grupos cargados.</Text>
                ) : (
                  <MundialMatchCalendar matches={groupMatches} teamsByCode={teamsByCode} />
                )}
              </SportsCard>
            </TabPanel>

            <TabPanel px={0} pt={6}>
              <KnockoutBracket teams={teams} matchesByRound={matchesByRound} />
            </TabPanel>

            <TabPanel px={0} pt={6}>
              <MundialHistorySection />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </SportsPageShell>
  );
};
